// TableProvider.tsx

import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	CustomUIEditingControls,
	EditingControls,
	HeaderBuiltins,
	RowAction,
	TableColumn,
	TableHeaderAction,
	TableParams,
} from '../types';
import {
	useColumnsVisibilityOverride,
	useEditableTableLogic,
	useHandleRowAction,
	usePrioritizeLogic,
	usePrioritizeOrder,
	useRowModalHandler,
	useStyledColumns,
	useTableMeta,
	useTableParams,
	useTableSearch,
} from '../hooks';
import { useThModalHandler } from '../hooks/useTHModalHandler';
import {
	createTableContext,
	createUseTableContext,
	TableContextType,
} from '../context/TableContext';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { EditableTableContext } from './EditableTableProvider';
import useTableRowsStore from '../hooks/useTableRowsStore';
import { usePageKey } from '@/shared/hooks';
import {
	usePageFiltersController,
	FilterOptionConfig,
} from '@/app/lib/page-filters/usePageFiltersController';
import { FiltersConfig } from '@/app/lib/page-filters/types';
import { useHasPermission } from '@/features/access-control/hooks/useHasPermission';
import { PERMISSIONS } from '@/shared/constants';
import { useStore } from '@/legacy/store/zustand';
import { stable } from '../utils';

export type TableProviderProps<TRow, TEditable extends FieldValues> = PropsWithChildren<{
	tableId: string;
	columns: TableColumn<TRow>[];
	extraHeaderActions?: TableHeaderAction[];
	rowKey?: string;
	rowActions?: RowAction[];
	extraRowHandlerContext?: Record<string, any>;
	/** optional editing controls to enable EditingProvider */
	editingControls?: EditingControls<TEditable>;
	costumUIEditingControls?: CustomUIEditingControls;
	tableDataEndpoint?: string;
	initialFilters?: Partial<TableParams>;
	/** ID to scope persisted params to a specific entity (e.g. userId, posId).
	 *  When this value changes, session params reset to defaults. */
	watchId?: string | number | boolean;
	/** whether to use cache on initial fetch (default: true) */
	initialCacheEnabled?: boolean;
	mergeGlobalFilter?: boolean;
	filtersConfig?: FiltersConfig;
	extraGlobalOptions?: Record<string, FilterOptionConfig>;
	/** whether the header's "edit table" (columns) button is shown (default: true) */
	showColumnsButton?: boolean;
}>;

const TableContext = createTableContext();
export const useTableContext = createUseTableContext(TableContext);

export const TableProvider = <TRow extends FieldValues, TEditable extends FieldValues = TRow>({
	children,
	tableId,
	columns,
	extraHeaderActions = [],
	rowKey = 'main_id',
	rowActions = [],
	extraRowHandlerContext = {},
	editingControls = undefined,
	costumUIEditingControls = undefined,
	tableDataEndpoint = '',
	initialFilters = {},
	initialCacheEnabled = true,
	watchId = undefined,
	mergeGlobalFilter = false,
	filtersConfig,
	extraGlobalOptions,
	showColumnsButton = true,
}: PropsWithChildren<TableProviderProps<TRow, TEditable>>) => {
	const [headerActionsOverride, _setHeaderActionsOverride] = useState<TableHeaderAction[] | null>(
		null,
	);
	const headerActions = headerActionsOverride ?? extraHeaderActions;

	const useCacheRef = useRef(initialCacheEnabled);
	const defaultBuiltins: HeaderBuiltins = {
		search: true,
		refresh: true,
		filter: true,
		columns: showColumnsButton,
	};
	const [headerBuiltins, setHeaderBuiltinsState] = useState<HeaderBuiltins>(defaultBuiltins);

	const setHeaderBuiltins = useCallback(
		(patch: Partial<HeaderBuiltins> | ((prev: HeaderBuiltins) => Partial<HeaderBuiltins>)) => {
			setHeaderBuiltinsState((prev) => ({
				...prev,
				...(typeof patch === 'function' ? patch(prev) : patch),
			}));
		},
		[],
	);
	const resetHeaderBuiltins = useCallback(() => setHeaderBuiltinsState(defaultBuiltins), []);

	const forceNextDbFetch = () => {
		useCacheRef.current = false;
	};

	const setCustomHeaderActions = useCallback((actions: TableHeaderAction[]) => {
		_setHeaderActionsOverride(actions);
	}, []);

	const rowModalHandler = useRowModalHandler(rowKey);
	const thModalHandler = useThModalHandler();

	const formMethods = useForm<TEditable>({
		resolver: editingControls ? zodResolver(editingControls?.schema) : undefined,
		defaultValues: {} as any,
	});

	const editTableLogic = useEditableTableLogic<TEditable>({ formMethods });

	const pageKey = usePageKey();
	const pageFiltersController = usePageFiltersController({
		filtersConfig: filtersConfig ?? {},
		pageKey,
		extraOptions: extraGlobalOptions,
	});

	const dateFilter = useStore((s) => s.filters[pageKey]?.date);
	const entityFilters = useStore((s) => s.filters[pageKey]?.entities);
	const sessionFilters = useStore((s) => s.sessionFilters?.[pageKey]);

	const globalFiltersSignature = useMemo(
		() => stable({ dateFilter, entityFilters, sessionFilters }),
		[dateFilter, entityFilters, sessionFilters],
	);

	const {
		tableParams,
		updateTableParams,
		handleTableChange,
		handleSearch,
		clearFilters,
		updateFilters,
		updateColumnsVisibility,
	} = useTableParams<TRow>(tableId, columns, { watchId, globalFiltersSignature }, initialFilters);

	const { searchTerm, handleSearchInputChange, clearSearch } = useTableSearch({
		onDebouncedSearch: (value) => handleSearch(value),
		initialValue: tableParams.search,
	});

	// Entering row-editing mode must show the unfiltered table, since edits
	// (e.g. adding rows) should apply to the full dataset, not a filtered view.
	// The filters/search that were active are kept here and reapplied once editing ends.
	const suspendedParamsRef = useRef<{ filters: TableParams['filters']; search: string } | null>(
		null,
	);

	useEffect(() => {
		if (editTableLogic.isEditing) {
			suspendedParamsRef.current = { filters: tableParams.filters, search: tableParams.search };
			updateTableParams({ filters: {}, search: '', page: 1 });
			clearSearch();
		} else if (suspendedParamsRef.current) {
			const { filters, search } = suspendedParamsRef.current;
			suspendedParamsRef.current = null;
			updateTableParams({ filters, search, page: 1 });
			handleSearchInputChange(search);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editTableLogic.isEditing]);

	const rowHandlerContext = {
		open: rowModalHandler.open,
		setIsEditing: editTableLogic?.setIsEditing,
		tableId,
		updateTableParams,
		forceNextDbFetch,
		...extraRowHandlerContext,
	};

	const handleRowAction = useHandleRowAction(rowHandlerContext);

	const prioritizeLogic = usePrioritizeLogic();

	const visibilityOverride = useColumnsVisibilityOverride({
		baseVisibility: tableParams.columnsVisibility,
	});

	const effectiveVisibility = visibilityOverride.effectiveVisibility;

	const { hasPermission: hasTableControlsPermission } = useHasPermission(PERMISSIONS.TABLE_CONTROL);

	const visibleColumns = useMemo(() => {
		if (!hasTableControlsPermission) return columns;
		const getKey = (c: TableColumn<any>) => String(c.key ?? c.dataIndex);
		return columns.filter((c) => effectiveVisibility[getKey(c)] ?? true);
	}, [columns, effectiveVisibility, hasTableControlsPermission]);

	const styledColumns = useStyledColumns({
		columns: visibleColumns,
		sortBy: tableParams.sortBy,
		direction: tableParams.direction,
		isEditing: editTableLogic?.isEditing,
		isAdding: editTableLogic?.isAdding,
		isPrioritizing: prioritizeLogic.isPrioritizing,
	});

	const editingControlsValue = editingControls
		? {
				schema: editingControls?.schema,
				onSubmit: editingControls.onSubmit ?? (() => {}),
				isPending: editingControls?.isPending ?? false,
				onCancel: editingControls?.onCancel,
			}
		: undefined;

	const tableMetaHandler = useTableMeta();

	const prioritizeOrderLogic = usePrioritizeOrder<TRow>((row) => row[rowKey] as string | number);

	const tableRowsStore = useTableRowsStore<TRow>();

	const tableContextValue: TableContextType<TRow> = {
		tableId,
		tableParams,
		updateTableParams,
		handleTableChange,
		handleSearch,
		clearFilters,
		clearSearch,
		searchTerm,
		handleSearchInputChange,
		rowModalHandler,
		thModalHandler,
		handleRowAction,
		styledColumns,
		columns,
		updateFilters,
		updateColumnsVisibility,
		customHeaderActions: headerActions,
		setCustomHeaderActions,
		rowKey,
		rowActions,
		tableDataEndpoint,
		forceNextDbFetch,
		useCacheRef,
		headerBuiltins: {
			...headerBuiltins,
			filter: headerBuiltins.filter && hasTableControlsPermission,
			columns: headerBuiltins.columns && hasTableControlsPermission,
		},
		setHeaderBuiltins,
		resetHeaderBuiltins,
		prioritizeLogic: {
			isPrioritizing: prioritizeLogic.isPrioritizing,
			enterPrioritize: prioritizeLogic.enterPrioritize,
			exitPrioritize: prioritizeLogic.exitPrioritize,
			togglePrioritize: prioritizeLogic.togglePrioritize,
			orderedIds: prioritizeOrderLogic.orderedIds,
			moveUp: prioritizeOrderLogic.moveUp,
			moveDown: prioritizeOrderLogic.moveDown,
			hasChanges: prioritizeOrderLogic.hasChanges,
			initFromRows: prioritizeOrderLogic.initFromRows as (rows: unknown[]) => void,
			setPosition: prioritizeOrderLogic.setPosition,
			indexOf: prioritizeOrderLogic.indexOf,
			total: prioritizeOrderLogic.total,
			orderedRows: prioritizeOrderLogic.orderedRows,
		},

		visibilityOverride,
		tableMetaHandler,
		tableRowsStore: {
			rows: tableRowsStore.rows,
			updatedAt: tableRowsStore.updatedAt,
			publish: tableRowsStore.publish as (newRows: unknown[], ts: number) => void,
		},
		mergeGlobalFilter,
		pageFiltersController,
	};

	if (!editingControlsValue) {
		return (
			<TableContext.Provider value={tableContextValue as TableContextType<unknown>}>
				{children}
			</TableContext.Provider>
		);
	}

	return (
		<FormProvider {...formMethods}>
			<EditableTableContext.Provider
				value={{
					formMethods,
					editTableLogic,
					editingControls: editingControlsValue,
					costumUIEditingControls,
				}}
			>
				<TableContext.Provider value={tableContextValue as TableContextType<unknown>}>
					{children}
				</TableContext.Provider>
			</EditableTableContext.Provider>
		</FormProvider>
	);
};
